/**
 * GoogleReviewBlock — v147.40 refactor:
 *   1. Star tap only SETS state — no auto-submit. Explicit Submit button.
 *   2. Comment box available on ALL ratings (1-5), not just below threshold.
 *   3. Remember returning visitors: load their prior rating on mount and
 *      pre-fill the stars/comment so they can update in place.
 *   4. Anonymous public reviews list rendered under the block (opt-in via
 *      `show_reviews` block config — defaults true).
 */
import { useEffect, useState } from "react";
import { Star, Send, MapPin, Lock, Loader2, Pencil, Copy, Check } from "lucide-react";
import { getCurrentPositionSafe, isGeolocationSupported } from "../../lib/geolocation";
import PhoneInput from "../PhoneInput";
import MarketingConsentCheckbox from "../MarketingConsentCheckbox";
import { _postPublicLead } from "./_shared";
import { openExternalUrl } from "../../lib/openExternal";

// Stable per-browser visitor id — shared with HubEngagementBar. Falls
// back to a fresh id when the storage key hasn't been set yet.
function getReviewVisitorId() {
  if (typeof window === "undefined") return null;
  try {
    let v = window.localStorage.getItem("hub_vid");
    if (!v) v = window.localStorage.getItem("storefront_vid");
    if (!v) {
      v = `rv_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      window.localStorage.setItem("hub_vid", v);
    }
    return v;
  } catch { return null; }
}

export function GoogleReviewBlock({ block, editable, onClick }) {
  const c = block.config || {};
  const mode = (c.mode || "gated").toLowerCase();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const visitorId = editable ? null : getReviewVisitorId();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | submitting | thanks | done
  const [thanksMsg, setThanksMsg] = useState("");
  const [error, setError] = useState("");
  const [priorRating, setPriorRating] = useState(null); // null | {rating, comment, updated_at}
  // v147.99.30 — Public reviews list removed from bio + action pages;
  // that surface is analytics-only for the shop owner. Kept
  // `priorRating` so returning visitors still see their previous stars
  // pre-selected on the widget.
  // v147.99.28 — When we redirect to Google we copy the visitor's own
  // comment to their clipboard so they can paste it into Google's review
  // form with one gesture (Google Reviews block query-param autofill
  // — see comment in postRating). We keep both the copied text + a
  // "copied" flash state so the thanks screen can show a Copy-again
  // affordance if the OS blocked the initial write.
  const [copiedComment, setCopiedComment] = useState("");
  const [copiedFlash, setCopiedFlash] = useState(false);
  const minRating = Math.max(1, Math.min(5, Number(c.min_rating_for_google) || 4));
  const collectEmail = (c.collect_email || "off").toLowerCase();
  const collectPhone = (c.collect_phone || "off").toLowerCase();

  // Path segments used by both GETs and the POST — /u/:slug or /u/:slug/:page.
  const parts = typeof window !== "undefined"
    ? window.location.pathname.split("/").filter(Boolean)
    : [];
  const creatorSlug = parts[1];
  const pageSlug = parts[2] || null;

  // v147.40 — Load the visitor's prior rating so returning visitors see
  // their previous stars pre-filled instead of a blank widget.
  useEffect(() => {
    if (editable || !creatorSlug || !visitorId || mode === "direct") return;
    let cancelled = false;
    (async () => {
      try {
        const url = pageSlug
          ? `${backend}/api/public/pages/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}/google-review-rating?block_id=${encodeURIComponent(block.block_id)}&visitor_id=${encodeURIComponent(visitorId)}`
          : `${backend}/api/public/creators/${encodeURIComponent(creatorSlug)}/google-review-rating?block_id=${encodeURIComponent(block.block_id)}&visitor_id=${encodeURIComponent(visitorId)}`;
        const r = await fetch(url);
        const d = await r.json().catch(() => ({}));
        if (!cancelled && d?.rating) {
          setPriorRating(d);
          setRating(d.rating);
          if (d.comment) setComment(d.comment);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [editable, creatorSlug, pageSlug, block.block_id, visitorId, backend, mode]);

  // v147.99.30 — Removed the anonymous public-reviews feed fetch. The
  // "What others are saying" list is no longer rendered on bio/action
  // pages; ratings surface only in the shop owner's Reviews Drill-down
  // modal inside CTA Analytics.

  // Direct mode short-circuit — no internal rating step.
  if (mode === "direct") {
    const href = c.google_review_url || "#";
    return (
      <a
        href={href}
        target={editable ? undefined : "_blank"}
        rel="noreferrer"
        onClick={(e) => { if (editable) { e.preventDefault(); return; } if (onClick) onClick(e, href); }}
        className="ah-block flex items-center gap-3 justify-center"
        data-testid={`block-google-review-${block.block_id}`}
      >
        <Star className="w-5 h-5 flex-shrink-0" fill="currentColor" strokeWidth={0} style={{ color: "#f59e0b" }} />
        <span className="font-bold text-sm">{c.cta_label || "Leave a Google review"}</span>
      </a>
    );
  }

  const onStarClick = (star) => {
    if (editable || status === "submitting") return;
    // Analytics ping fires immediately so brands still see engagement
    // heatmap on unfinished ratings.
    if (onClick) {
      try { onClick({ preventDefault: () => {} }, `rating:${star}`); } catch { /* ignore */ }
    }
    setRating(star);
    // v147.99.30 — Above-threshold ratings skip the comment box + Submit
    // button entirely and fast-track straight to Google. Comments are
    // only useful to the brand privately for LOW ratings; for good
    // ratings we send visitors to Google as quickly as possible so
    // they don't drop off between tapping a star and writing prose.
    // We do the clipboard pre-copy synchronously here so iOS Safari
    // allows the write from the still-fresh user activation.
    if (star >= minRating) {
      if (collectEmail === "required" || collectPhone === "required") {
        // Brand demanded contact info before Google redirect — keep the
        // form path so we can still gate on those inputs.
        return;
      }
      postRating(star, "", "", "");
    }
  };

  const postRating = async (star, cmt, extraEmail, extraPhone) => {
    setStatus("submitting");
    setError("");
    try {
      const url = pageSlug
        ? `${backend}/api/public/pages/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(pageSlug)}/google-review-rating`
        : `${backend}/api/public/creators/${encodeURIComponent(creatorSlug)}/google-review-rating`;
      const res = await fetch(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            block_id: block.block_id,
            rating: star,
            comment: cmt || null,
            email: extraEmail || null,
            phone: extraPhone || null,
            marketing_consent: marketingConsent,
            visitor_id: visitorId,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (data?.should_redirect_to_google && data?.google_url) {
        // v147.99.28 — Google's review form doesn't accept prefill query
        // params (they intentionally block that to prevent review farming),
        // so the best UX is to have the comment already on the clipboard
        // (we wrote it in trySubmit while user-activation was fresh) and
        // remind the visitor to reuse the same star count. On Google
        // they long-press → Paste (or Cmd/Ctrl+V) and hit Submit.
        const commentText = (cmt || "").trim();
        if (commentText) {
          // Retry once to catch browsers where the sync fire-and-forget
          // race lost — the paste hint UI still shows the text either way.
          setCopiedComment(commentText);
          try {
            if (navigator?.clipboard?.writeText) {
              await navigator.clipboard.writeText(commentText);
              setCopiedFlash(true);
            }
          } catch { /* Copy-again button on thanks screen covers it */ }
        }
        setStatus("done");
        setThanksMsg(
          commentText
            ? "We copied your comment. Paste it on Google and submit."
            : "Thanks! Opening Google…"
        );
        openExternalUrl(data.google_url);
      } else {
        setStatus("thanks");
        setThanksMsg(data?.thanks_message || c.thanks_message || (priorRating ? "Your review has been updated." : "Thanks for your feedback!"));
      }
    } catch {
      setStatus("thanks");
      setThanksMsg(c.thanks_message || "Thanks for your feedback!");
    }
  };

  const trySubmit = () => {
    if (rating < 1) {
      setError("Tap a star first.");
      return;
    }
    if (collectEmail === "required" && !email.trim()) {
      setError("Please enter your email to continue.");
      return;
    }
    if (collectPhone === "required" && !phone.trim()) {
      setError("Please enter your phone number to continue.");
      return;
    }
    setError("");
    const trimmedComment = comment.trim();
    // v147.99.28 — Pre-copy synchronously during the user-activation
    // gesture so iOS Safari / Android WebViews allow the write. We
    // still update state; the redirect path (inside postRating) shows
    // the paste hint. If the visitor ends up NOT being redirected
    // (rating < min_for_google) we simply leave the clipboard as-is.
    if (trimmedComment) {
      setCopiedComment(trimmedComment);
      try {
        if (navigator?.clipboard?.writeText) {
          // Not awaited — fire-and-forget so user activation stays hot.
          navigator.clipboard.writeText(trimmedComment).catch(() => {});
        }
      } catch { /* silent */ }
    }
    postRating(rating, trimmedComment, email.trim() || "", phone.trim() || "");
  };

  if (status === "thanks" || status === "done") {
    const showCopyHelper = status === "done" && !!copiedComment;
    const doCopy = async () => {
      try {
        await navigator.clipboard.writeText(copiedComment);
        setCopiedFlash(true);
        setTimeout(() => setCopiedFlash(false), 1500);
      } catch { /* ignore — user can select + copy manually */ }
    };
    return (
      <div className="ah-block text-center" data-testid={`block-google-review-thanks-${block.block_id}`}>
        <div className="flex justify-center gap-1 mb-2" aria-hidden="true">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className="w-5 h-5"
              fill={n <= rating ? "currentColor" : "none"}
              strokeWidth={2}
              style={{ color: n <= rating ? "#f59e0b" : "var(--ah-fg)" }}
            />
          ))}
        </div>
        <p className="text-sm font-bold">{thanksMsg}</p>
        {showCopyHelper && (
          <div
            className="mt-3 border-2 text-left p-2.5 space-y-2"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-bg)" }}
            data-testid={`block-google-review-copy-helper-${block.block_id}`}
          >
            <p className="text-[11px] font-black uppercase tracking-wide" style={{ color: "var(--ah-fg)" }}>
              On Google, tap {rating}★ then paste this
            </p>
            <p
              className="text-xs whitespace-pre-line p-2 border font-mono"
              style={{ color: "var(--ah-fg)", borderColor: "var(--ah-fg)", background: "var(--ah-block-bg)" }}
              data-testid={`block-google-review-copied-text-${block.block_id}`}
            >
              {copiedComment}
            </p>
            <button
              type="button"
              onClick={doCopy}
              className="w-full border-2 font-black text-xs py-1.5 inline-flex items-center justify-center gap-1.5"
              style={{ borderColor: "var(--ah-fg)", background: copiedFlash ? "var(--ah-accent)" : "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
              data-testid={`block-google-review-copy-btn-${block.block_id}`}
            >
              {copiedFlash ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copiedFlash ? "Copied — now paste on Google" : "Copy again"}
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => { setStatus("idle"); setError(""); }}
          className="mt-2 text-xs underline"
          style={{ color: "var(--ah-muted)" }}
          data-testid={`block-google-review-change-${block.block_id}`}
        >
          <Pencil className="w-3 h-3 inline -mt-0.5 mr-1" />
          Change your rating
        </button>
        {/* v147.99.30 — Public review list hidden on bio/action pages.
            Reviews are analytics-only for the shop owner via the CTA
            Analytics Reviews Drill-down modal. */}
      </div>
    );
  }

  return (
    <div className="ah-block text-center" data-testid={`block-google-review-${block.block_id}`}>
      {c.heading && <p className="font-bold text-base">{c.heading}</p>}
      {c.subtext && <p className="text-xs ah-muted mt-1">{c.subtext}</p>}
      {/* v147.99.30 — Removed the "You rated N★ · tap a star to change"
          hint. It exposed prior-rating state to public visitors and
          added visual noise; visitors can still update by tapping a
          new star (backend upserts on visitor_id + block_id). */}
      <div className="flex justify-center gap-1 mt-3" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onStarClick(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            disabled={editable || status === "submitting"}
            className="p-1 transition-transform hover:scale-110 disabled:opacity-60"
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={`block-google-review-star-${block.block_id}-${n}`}
          >
            <Star
              className="w-8 h-8"
              fill={n <= (hover || rating) ? "currentColor" : "none"}
              strokeWidth={2}
              style={{ color: n <= (hover || rating) ? "#f59e0b" : "var(--ah-fg)" }}
            />
          </button>
        ))}
      </div>

      {/* v147.99.30 — Comment box only shown for LOW ratings (private
          feedback for the brand). Above-threshold ratings auto-submit
          in onStarClick and skip this UI entirely, so the visitor is
          redirected to Google without any extra typing. */}
      {rating > 0 && rating < minRating && (
        <div className="mt-3 space-y-2 text-left" data-testid={`block-google-review-form-${block.block_id}`}>
          <p className="text-xs ah-muted">
            Anything we could do better? (private — only the owner sees this)
          </p>
          <textarea
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional private feedback…"
            className="w-full border-2 px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`block-google-review-comment-${block.block_id}`}
          />
          {collectEmail !== "off" && (
            <>
              <p className="text-xs font-bold">
                Email {collectEmail === "required" ? "" : "(optional)"}
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required={collectEmail === "required"}
                className="w-full border-2 px-2 py-1 text-sm bg-transparent"
                style={{ borderColor: "var(--ah-fg)" }}
                data-testid={`block-google-review-email-${block.block_id}`}
              />
            </>
          )}
          {collectPhone !== "off" && (
            <>
              <p className="text-xs font-bold">
                Phone {collectPhone === "required" ? "" : "(optional)"}
              </p>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                required={collectPhone === "required"}
                placeholder="10-digit mobile number"
                data-testid={`block-google-review-phone-${block.block_id}`}
                className="w-full"
              />
              <MarketingConsentCheckbox
                checked={marketingConsent}
                onChange={setMarketingConsent}
                testid={`block-google-review-consent-${block.block_id}`}
                compact
              />
            </>
          )}
          {error && (
            <p className="text-xs font-bold text-red-700" data-testid={`block-google-review-error-${block.block_id}`}>
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={trySubmit}
            disabled={editable || status === "submitting" || rating < 1}
            className="w-full border-2 font-bold text-sm py-1.5 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
            data-testid={`block-google-review-submit-${block.block_id}`}
          >
            {status === "submitting" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {priorRating ? "Update review" : "Submit review"}
          </button>
        </div>
      )}

      {/* v147.99.30 — Rare edge case: brand requires email/phone AND
          the visitor picked an above-threshold rating. Show a minimal
          contact form (no comment box) so we can still collect the
          required field before the Google redirect. */}
      {rating >= minRating && (collectEmail === "required" || collectPhone === "required") && (
        <div className="mt-3 space-y-2 text-left" data-testid={`block-google-review-form-${block.block_id}`}>
          {collectEmail === "required" && (
            <>
              <p className="text-xs font-bold">Email</p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full border-2 px-2 py-1 text-sm bg-transparent"
                style={{ borderColor: "var(--ah-fg)" }}
                data-testid={`block-google-review-email-${block.block_id}`}
              />
            </>
          )}
          {collectPhone === "required" && (
            <>
              <p className="text-xs font-bold">Phone</p>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                required
                placeholder="10-digit mobile number"
                data-testid={`block-google-review-phone-${block.block_id}`}
                className="w-full"
              />
              <MarketingConsentCheckbox
                checked={marketingConsent}
                onChange={setMarketingConsent}
                testid={`block-google-review-consent-${block.block_id}`}
                compact
              />
            </>
          )}
          {error && (
            <p className="text-xs font-bold text-red-700" data-testid={`block-google-review-error-${block.block_id}`}>
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={trySubmit}
            disabled={editable || status === "submitting"}
            className="w-full border-2 font-bold text-sm py-1.5 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
            data-testid={`block-google-review-submit-${block.block_id}`}
          >
            {status === "submitting" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Continue to Google
          </button>
        </div>
      )}

      {status === "submitting" && (
        <p className="text-xs ah-muted mt-2 flex items-center justify-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>
      )}
      {/* v147.99.30 — Public "What others are saying" list removed from
          bio + action pages. That surface is now analytics-only for the
          shop owner via the Reviews Drill-down modal in CTA Analytics. */}
    </div>
  );
}


/* --------------------------------------------------------------------
 * v147.99.30 — `ReviewsList` component + `relTime` helper removed. The
 * public review feed is intentionally hidden on bio + action pages;
 * ratings are analytics-only for the shop owner via the CTA Analytics
 * Reviews Drill-down modal.
 * ------------------------------------------------------------------ */


// ---------------------------------------------------------------------------
// v89 (Feb 2026) — PageGateBlock
// ---------------------------------------------------------------------------
// Renders a form that the visitor MUST submit before the rest of the page's
// blocks become visible. Submissions are stored as leads with intent="gate".
// The parent (`PublicActionPage`) manages the actual hide/show logic; this
// component just calls `onUnlock()` on success.
// ---------------------------------------------------------------------------
export function PageGateBlock({ block, editable, onUnlock }) {
  const c = block.config || {};
  const gateType = (c.gate_type || "location_form").toLowerCase();
  const [state, setState] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | done
  const [error, setError] = useState("");

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const doSubmit = async (payload) => {
    if (editable) return;
    setStatus("submitting");
    setError("");
    try {
      await _postPublicLead(block, { ...payload, intent: "gate" });
      setStatus("done");
      if (onUnlock) onUnlock();
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  const submitForm = (e) => {
    e?.preventDefault();
    if (editable) return;
    if (gateType === "location_form") {
      if (!state.city) { setError("Please enter your city"); return; }
      doSubmit({ name: state.name || null,
        extra: { gate_type: "location_form", city: state.city, area: state.area || null } });
    } else if (gateType === "phone") {
      if (!state.phone) { setError("Please enter your phone number"); return; }
      doSubmit({ phone: state.phone,
        marketing_consent: state.marketing_consent !== false,
        extra: { gate_type: "phone" } });
    } else if (gateType === "email") {
      if (!state.email || !state.email.includes("@")) { setError("Please enter a valid email"); return; }
      doSubmit({ email: state.email, extra: { gate_type: "email" } });
    } else if (gateType === "lead_form") {
      const fields = c.fields && c.fields.length ? c.fields : ["name", "email"];
      const missing = fields.find((f) => !state[f]);
      if (missing) { setError(`Please fill in ${missing}`); return; }
      const extra = { gate_type: "lead_form" };
      fields.forEach((f) => {
        if (!["name", "email", "phone"].includes(f)) extra[f] = state[f];
      });
      doSubmit({
        name: state.name || null,
        email: state.email || null,
        phone: state.phone || null,
        marketing_consent: state.marketing_consent !== false,
        extra,
      });
    } else if (gateType === "choice") {
      if (!state.choice) { setError("Please pick an option"); return; }
      doSubmit({ extra: { gate_type: "choice", question: c.question || null, choice: state.choice } });
    }
  };

  const askForGeo = () => {
    if (editable) return;
    setError("");
    if (!isGeolocationSupported()) { setError("Location isn't supported by your browser"); return; }
    setStatus("submitting");
    getCurrentPositionSafe(
      async (pos) => {
        try {
          await _postPublicLead(block, {
            block_id: block.block_id,
            intent: "gate",
            extra: {
              gate_type: "geolocation",
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            },
          });
          setStatus("done");
          if (onUnlock) onUnlock();
        } catch {
          setStatus("idle");
          setError("Could not save your location. Please try again.");
        }
      },
      () => {
        setStatus("idle");
        setError("Please allow location access to continue.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  return (
    <div className="ah-block" data-testid={`block-page-gate-${block.block_id}`}>
      <div className="flex items-center gap-2 justify-center mb-2">
        <Lock className="w-5 h-5 ah-accent" strokeWidth={2.5} />
        <p className="font-bold text-base">{c.heading || "Unlock the offers"}</p>
      </div>
      {c.subtext && <p className="text-xs ah-muted text-center mb-3">{c.subtext}</p>}
      <form onSubmit={submitForm} className="space-y-2">
        {gateType === "geolocation" && (
          <button
            type="button"
            onClick={askForGeo}
            disabled={editable || status === "submitting"}
            className="w-full border-2 font-bold text-sm py-2 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
            data-testid={`block-page-gate-geo-btn-${block.block_id}`}
          >
            {status === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            {c.cta_label || "Share my location"}
          </button>
        )}
        {gateType === "location_form" && (
          <>
            <input
              type="text" placeholder="Your city" required
              value={state.city || ""}
              onChange={(e) => set("city", e.target.value)}
              className="w-full border-2 px-2 py-1 text-sm bg-transparent"
              style={{ borderColor: "var(--ah-fg)" }}
              data-testid={`block-page-gate-city-${block.block_id}`}
            />
            <input
              type="text" placeholder="Area (optional)"
              value={state.area || ""}
              onChange={(e) => set("area", e.target.value)}
              className="w-full border-2 px-2 py-1 text-sm bg-transparent"
              style={{ borderColor: "var(--ah-fg)" }}
              data-testid={`block-page-gate-area-${block.block_id}`}
            />
          </>
        )}
        {gateType === "phone" && (
          <>
            <PhoneInput
              value={state.phone || ""}
              onChange={(v) => set("phone", v)}
              required
              placeholder="10-digit mobile number"
              data-testid={`block-page-gate-phone-${block.block_id}`}
              className="w-full"
            />
            <MarketingConsentCheckbox
              checked={state.marketing_consent !== false}
              onChange={(v) => set("marketing_consent", v)}
              testid={`block-page-gate-consent-${block.block_id}`}
              compact
            />
          </>
        )}
        {gateType === "email" && (
          <input
            type="email" placeholder="you@example.com" required
            value={state.email || ""}
            onChange={(e) => set("email", e.target.value)}
            className="w-full border-2 px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`block-page-gate-email-${block.block_id}`}
          />
        )}
        {gateType === "lead_form" && (
          <>
            {(c.fields && c.fields.length ? c.fields : ["name", "email"]).map((f) => (
              <input
                key={f}
                type={f === "email" ? "email" : f === "phone" ? "tel" : "text"}
                placeholder={f.charAt(0).toUpperCase() + f.slice(1)}
                required
                value={state[f] || ""}
                onChange={(e) => set(f, e.target.value)}
                className="w-full border-2 px-2 py-1 text-sm bg-transparent"
                style={{ borderColor: "var(--ah-fg)" }}
                data-testid={`block-page-gate-lead-${f}-${block.block_id}`}
              />
            ))}
            {/* v146.44 — Show consent only when this gate collects a phone. */}
            {(c.fields && c.fields.length ? c.fields : ["name", "email"]).includes("phone") && (
              <MarketingConsentCheckbox
                checked={state.marketing_consent !== false}
                onChange={(v) => set("marketing_consent", v)}
                testid={`block-page-gate-consent-${block.block_id}`}
                compact
              />
            )}
          </>
        )}
        {gateType === "choice" && (
          <>
            {c.question && <p className="text-xs font-bold text-center mb-1">{c.question}</p>}
            <select
              value={state.choice || ""}
              onChange={(e) => set("choice", e.target.value)}
              required
              className="w-full border-2 px-2 py-1 text-sm bg-transparent"
              style={{ borderColor: "var(--ah-fg)" }}
              data-testid={`block-page-gate-choice-${block.block_id}`}
            >
              <option value="">— Select —</option>
              {(c.choices || []).map((ch, i) => (
                <option key={i} value={ch}>{ch}</option>
              ))}
            </select>
          </>
        )}
        {gateType !== "geolocation" && (
          <button
            type="submit"
            disabled={editable || status === "submitting"}
            className="w-full border-2 font-bold text-sm py-2 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
            data-testid={`block-page-gate-submit-${block.block_id}`}
          >
            {status === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {c.cta_label || "Unlock"}
          </button>
        )}
        {error && (
          <p className="text-xs font-bold text-center" style={{ color: "#dc2626" }}
             data-testid={`block-page-gate-error-${block.block_id}`}>
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

